/**
 * Whether this copy of the application is older than the oldest one the API
 * still accepts.
 *
 * THE RULE THAT MATTERS MOST: any doubt opens the application. A version that
 * cannot be read, on either side, is never a reason to block somebody. The
 * blocking screen has no way out, so the one mistake it must never make is to
 * appear when nobody asked for it.
 */

export type StorePlatform = 'ios' | 'android';

/** The same shape the API accepts: three numbers, nothing else. */
const REQUIRED_PATTERN = /^\d{1,4}\.\d{1,4}\.\d{1,4}$/;

/** The installed version can carry more than three parts; only those count. */
const INSTALLED_PATTERN = /^(\d+)(?:\.(\d+))?(?:\.(\d+))?/;

/**
 * The Apple ID of Smatchy, for opening its App Store page directly.
 *
 * Without it the plugin looks the listing up by bundle identifier, which is one
 * more network call at the moment the member is waiting for the store. Checked
 * on 18 October 2026: itunes.apple.com/lookup?bundleId=com.smatchy.app answers
 * this ID in the US and the French store.
 */
export const SMATCHY_APP_STORE_ID = '6473653332';

/** The three numbers of a version, once `accepted` has said it is one. */
const parts = (version: string, accepted: RegExp): number[] | null => {
  const trimmed = version.trim();
  const match = accepted.test(trimmed) ? trimmed.match(INSTALLED_PATTERN) : null;
  if (!match) {
    return null;
  }
  return [match[1], match[2], match[3]].map((part) => Number(part ?? 0));
};

/** The version the API requires for this store, or null when it said nothing usable. */
export const readRequiredVersion = (answer: unknown, platform: StorePlatform): string | null => {
  const value = (answer as Partial<Record<StorePlatform, unknown>> | null | undefined)?.[platform];
  return typeof value === 'string' && REQUIRED_PATTERN.test(value.trim()) ? value.trim() : null;
};

/** True only when both versions are readable and the installed one is strictly older. */
export const isOlderThan = (installed: string, required: string): boolean => {
  const mine = parts(installed, INSTALLED_PATTERN);
  const floor = parts(required, REQUIRED_PATTERN);
  if (!mine || !floor) {
    return false;
  }
  for (let index = 0; index < 3; index += 1) {
    if (mine[index] !== floor[index]) {
      return mine[index] < floor[index];
    }
  }
  return false;
};
